import { useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, Download, History, Loader2 } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { useToast } from "~/hooks/use-toast";
import { exportUnifiedKardexToExcel } from "~/services/wms/unifiedKardexExportService";

import { UnifiedKardexTab } from "./components/movements";

import type { UnifiedKardexFilters } from "~/services/wms/unifiedKardex";


export function MovementsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<UnifiedKardexFilters>({});
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportUnifiedKardexToExcel(filters);
      toast({
        title: "Exportación exitosa",
        description: "El archivo se ha descargado correctamente.",
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Error al exportar",
        description: "Hubo un problema al generar el archivo Excel.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Kardex"
        description="Trazabilidad de todos los movimientos de inventario — productos, insumos y aceites — con saldo corrido"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <Button
            onClick={handleExport}
            disabled={isExporting}
            variant="outline"
            className="gap-2"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Exportar Excel
          </Button>
        }
      />

      <UnifiedKardexTab filters={filters} onFiltersChange={setFilters} />

      {/* Help Text */}
      <div className="rounded-lg border bg-muted/30 p-4">
        <h3 className="flex items-center gap-2 font-medium">
          <History className="h-4 w-4" />
          Sobre el Kardex
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Un único historial con todos los movimientos de inventario: productos
          terminados, insumos (materia prima) y aceites. Incluye entradas
          (producción, recepciones de compra), salidas (despachos, consumos de
          producción), traslados y ajustes, con saldo corrido por ítem. Busca
          por código o nombre y usa el filtro <strong>Tipo de ítem</strong> para
          acotar a Producto, Insumo o Aceite, o <strong>Ubicación</strong> para
          una bodega (B1/B2) o planta (P1/P2).
        </p>
      </div>
    </PageContainer>
  );
}

export default MovementsPage;
